import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../apiClient";
import type { MembershipStatus, OrgRole, PageScope } from "../../utils/types";

export type OrgUserGroup = { id: number; name: string };

export type OrgUser = {
  id: number;
  username: string;
  email: string;
  org_role: OrgRole | null;
  phone_number: string | null;
  membership_status: MembershipStatus;
  page_scopes: PageScope[];
  assigned_groups: OrgUserGroup[];
};

export type OrgInvitation = {
  id: number;
  email: string;
  org_role: OrgRole;
  phone_number: string | null;
  page_scopes: PageScope[];
  status: string;
  expires_at: string;
  created_at: string;
};

export const orgUsersKeys = {
  all: ["orgUsers"] as const,
};

export type MentionableOrgUser = {
  id: number;
  username: string;
  email: string;
};

export const mentionableOrgUsersKeys = {
  all: ["mentionableOrgUsers"] as const,
};

export function useMentionableOrgUsersQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: mentionableOrgUsersKeys.all,
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await apiClient.get<MentionableOrgUser[]>(
        "/org/users/mentionable",
      );
      return data;
    },
  });
}

export function useOrgUsersQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: orgUsersKeys.all,
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      const { data } = await apiClient.get<{
        members: OrgUser[];
        pending_invitations: OrgInvitation[];
      }>("/org/users");
      return data;
    },
  });
}

export function useInviteOrgUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      email: string;
      org_role: OrgRole;
      phone_number?: string;
      page_scopes: PageScope[];
    }) => {
      const { data } = await apiClient.post("/org/users/invite", payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: orgUsersKeys.all }),
  });
}

export function useUpdateOrgUserMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      ...payload
    }: {
      userId: number;
      org_role?: OrgRole;
      phone_number?: string | null;
      page_scopes?: PageScope[];
      membership_status?: MembershipStatus;
    }) => {
      const { data } = await apiClient.patch(`/org/users/${userId}`, payload);
      return data as OrgUser;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: orgUsersKeys.all }),
  });
}

export function useAssignCollectorGroupsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      group_ids,
    }: {
      userId: number;
      group_ids: number[];
    }) => {
      const { data } = await apiClient.put(`/org/users/${userId}/groups`, {
        group_ids,
      });
      return data as OrgUserGroup[];
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: orgUsersKeys.all }),
  });
}

export function useResendInviteMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: number) => {
      await apiClient.post(`/org/users/invites/${inviteId}/resend`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: orgUsersKeys.all }),
  });
}

export function useCancelInviteMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: number) => {
      await apiClient.delete(`/org/users/invites/${inviteId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: orgUsersKeys.all }),
  });
}

export function useValidateInviteQuery(token: string | null) {
  return useQuery({
    queryKey: ["orgInviteValidate", token],
    enabled: Boolean(token),
    queryFn: async () => {
      const { data } = await apiClient.get<{
        org_id: number;
        org_name: string;
        org_role: OrgRole;
        email: string;
        expired: boolean;
      }>(`/org/invites/validate?token=${encodeURIComponent(token!)}`);
      return data;
    },
  });
}

export function useAcceptInviteMutation() {
  return useMutation({
    mutationFn: async (token: string) => {
      const { data } = await apiClient.post<{
        ok: boolean;
        org_id: number | null;
        org_role: OrgRole | null;
      }>("/org/invites/accept", { token });
      return data;
    },
  });
}
